import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdCheck } from 'react-icons/md';
import '../master/MasterData.css';

interface Customer {
  customerId: number;
  customerCode: string;
  customerName: string;
  customerType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface Collection {
  collectionId: number;
  collectionNo: string;
  collectionDate: string;
  customerCode: string;
  customerName: string;
  invoiceNo: string;
  invoiceAmount: number;
  paymentAmount: number;
  paymentMethod: 'CASH' | 'CHECK' | 'BANK_TRANSFER' | 'CREDIT_CARD';
  referenceNo: string;
  bankName: string;
  checkNo: string;
  checkDate: string;
  status: 'PENDING' | 'CLEARED' | 'BOUNCED';
  remarks: string;
}

const Collections: React.FC = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch customers from API
    setCustomers([
      {
        customerId: 1,
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerType: 'CORPORATE',
        tin: '123-456-789-000',
        email: 'sm@furniture.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        customerId: 2,
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerType: 'CORPORATE',
        tin: '987-654-321-000',
        email: 'homedepot@example.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch collections from API
    setCollections([
      {
        collectionId: 1,
        collectionNo: 'COL-2025-0001',
        collectionDate: '2025-02-20',
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        invoiceNo: 'INV-2025-0002',
        invoiceAmount: 850000,
        paymentAmount: 850000,
        paymentMethod: 'BANK_TRANSFER',
        referenceNo: 'TRF-12345',
        bankName: 'BDO',
        checkNo: '',
        checkDate: '',
        status: 'CLEARED',
        remarks: 'Full payment received'
      },
      {
        collectionId: 2,
        collectionNo: 'COL-2025-0002',
        collectionDate: '2025-02-25',
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        invoiceNo: 'INV-2025-0001',
        invoiceAmount: 450000,
        paymentAmount: 225000,
        paymentMethod: 'CHECK',
        referenceNo: '',
        bankName: 'BPI',
        checkNo: 'CHK-789012',
        checkDate: '2025-02-25',
        status: 'PENDING',
        remarks: 'Partial payment - 50%'
      }
    ]);
  }, []);

  const filteredCollections = collections.filter(collection =>
    collection.collectionNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    collection.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    collection.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingCollection(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (collection: Collection) => {
    setEditingCollection(collection);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (collection: Collection) => {
    setEditingCollection(collection);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (collectionId: number) => {
    if (window.confirm('Are you sure you want to delete this collection?')) {
      setCollections(collections.filter(c => c.collectionId !== collectionId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'CLEARED': return 'badge-success';
      case 'PENDING': return 'badge-warning';
      case 'BOUNCED': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'CASH': return 'badge-success';
      case 'CHECK': return 'badge-info';
      case 'BANK_TRANSFER': return 'badge-primary';
      case 'CREDIT_CARD': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Collections</h2>
            <p className="subtitle">Record customer payments and manage receivables</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Record New Collection
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search collections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Collection No</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Invoice No</th>
                  <th>Invoice Amt</th>
                  <th>Payment Amt</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCollections.map(collection => (
                  <tr key={collection.collectionId}>
                    <td className="code">{collection.collectionNo}</td>
                    <td>{new Date(collection.collectionDate).toLocaleDateString('en-US')}</td>
                    <td>{collection.customerName}</td>
                    <td>{collection.invoiceNo}</td>
                    <td className="amount">₱{collection.invoiceAmount.toLocaleString()}</td>
                    <td className="amount">₱{collection.paymentAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getPaymentMethodBadge(collection.paymentMethod)}`}>
                        {collection.paymentMethod.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(collection.status)}`}>
                        {collection.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(collection)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(collection)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(collection.collectionId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total Collected:</td>
                  <td colSpan={4} style={{ fontWeight: 'bold' }}>
                    ₱{collections.reduce((sum, c) => sum + c.paymentAmount, 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {filteredCollections.length === 0 && (
              <div className="no-data">
                <p>No collections found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <CollectionModal
          collection={editingCollection}
          viewMode={viewMode}
          customers={customers}
          onClose={() => setShowModal(false)}
          onSave={(collection) => {
            if (editingCollection) {
              setCollections(collections.map(c =>
                c.collectionId === collection.collectionId ? collection : c
              ));
            } else {
              setCollections([...collections, { ...collection, collectionId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface CollectionModalProps {
  collection: Collection | null;
  viewMode: boolean;
  customers: Customer[];
  onClose: () => void;
  onSave: (collection: Collection) => void;
}

const CollectionModal: React.FC<CollectionModalProps> = ({ collection, viewMode, customers, onClose, onSave }) => {
  const [formData, setFormData] = useState<Collection>(
    collection || {
      collectionId: 0,
      collectionNo: '',
      collectionDate: new Date().toISOString().split('T')[0],
      customerCode: '',
      customerName: '',
      invoiceNo: '',
      invoiceAmount: 0,
      paymentAmount: 0,
      paymentMethod: 'CASH',
      referenceNo: '',
      bankName: '',
      checkNo: '',
      checkDate: '',
      status: 'PENDING',
      remarks: ''
    }
  );

  const handleCustomerChange = (customerCode: string) => {
    const selectedCustomer = customers.find(c => c.customerCode === customerCode);
    if (selectedCustomer) {
      setFormData({
        ...formData,
        customerCode: selectedCustomer.customerCode,
        customerName: selectedCustomer.customerName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Collection' : collection ? 'Edit Collection' : 'Record New Collection'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Collection No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.collectionNo}
                  onChange={(e) => setFormData({ ...formData, collectionNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Collection Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.collectionDate}
                  onChange={(e) => setFormData({ ...formData, collectionDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PENDING">Pending</option>
                  <option value="CLEARED">Cleared</option>
                  <option value="BOUNCED">Bounced</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Customer Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.customerCode}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">Select Customer</option>
                  {customers.filter(c => c.isActive === 'Y').map(customer => (
                    <option key={customer.customerId} value={customer.customerCode}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Invoice No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.invoiceNo}
                  onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                  placeholder="INV-2025-XXXX"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Invoice Amount *</label>
                <input
                  type="number"
                  required
                  disabled={viewMode}
                  value={formData.invoiceAmount}
                  onChange={(e) => setFormData({ ...formData, invoiceAmount: parseFloat(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>Payment Amount *</label>
                <input
                  type="number"
                  required
                  disabled={viewMode}
                  value={formData.paymentAmount}
                  onChange={(e) => setFormData({ ...formData, paymentAmount: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Payment Details</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    required
                    disabled={viewMode}
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Reference No</label>
                  <input
                    type="text"
                    disabled={viewMode}
                    value={formData.referenceNo}
                    onChange={(e) => setFormData({ ...formData, referenceNo: e.target.value })}
                    placeholder="Transaction/Authorization No"
                  />
                </div>
              </div>

              {(formData.paymentMethod === 'CHECK' || formData.paymentMethod === 'BANK_TRANSFER') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Bank Name {formData.paymentMethod === 'CHECK' ? '*' : ''}</label>
                    <input
                      type="text"
                      required={formData.paymentMethod === 'CHECK'}
                      disabled={viewMode}
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="e.g., BDO, BPI, Metrobank"
                    />
                  </div>
                  {formData.paymentMethod === 'CHECK' && (
                    <div className="form-group">
                      <label>Check No *</label>
                      <input
                        type="text"
                        required
                        disabled={viewMode}
                        value={formData.checkNo}
                        onChange={(e) => setFormData({ ...formData, checkNo: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              )}

              {formData.paymentMethod === 'CHECK' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Check Date *</label>
                    <input
                      type="date"
                      lang="en-US"
                      required
                      disabled={viewMode}
                      value={formData.checkDate}
                      onChange={(e) => setFormData({ ...formData, checkDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group"></div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional notes about this payment..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {collection ? 'Update' : 'Record'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Collections;
